import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import Chart from 'react-apexcharts';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useReportes } from './ReportesContex';
import '../../styles/reportes.css';

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

const ESTATUS_LABEL = { 0: 'Fuera', 1: 'Operando', 2: 'Setup', 3: 'Paro Planeado', 4: 'Paro no Planeado', 5: 'Paro' };

// Mismo orden de colores que la función colors[] de la gráfica
const STATUS_ORDER = [1, 2, 3, 4, 5, 0];

function msToHM(ms) {
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
}

async function exportarExcel(statusLinea, selectedArea, selectedLinea) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const toArgb  = (hex) => `FF${hex.toUpperCase()}`;
    const primary  = getPrimaryColor();
    const headerBg = blendHex(primary, '000000', 0.8);
    const infoBg   = blendHex(primary, 'ffffff', 0.85);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Línea de Tiempo');

    const infoText = [
        selectedArea?.nombre  ? `Área: ${selectedArea.nombre}`   : '',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : '',
    ].filter(Boolean).join('   |   ');
    if (infoText) {
        ws.mergeCells('A1:F1');
        ws.getCell('A1').value = infoText;
        ws.getCell('A1').style = {
            font: { size: 11 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } },
        };
        ws.getRow(1).height = 18;
    }

    const hdrStyle = { font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } }, alignment: { horizontal: 'center', vertical: 'middle' } };
    const hdrs = ['Línea', 'Estatus', 'Folio', 'Fecha Inicio', 'Fecha Fin', 'Duración'];
    const hdrRow = ws.getRow(infoText ? 2 : 1);
    hdrs.forEach((h, i) => { hdrRow.getCell(i + 1).value = h; hdrRow.getCell(i + 1).style = hdrStyle; });
    hdrRow.height = 20;
    [20, 20, 12, 22, 22, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    let rowIdx = infoText ? 3 : 2;
    let altToggle = false;
    statusLinea.forEach((linea) => {
        linea.datos.forEach((dato) => {
            const row = ws.getRow(rowIdx);
            const ini = new Date(dato.fechaHoraIni);
            const fin = new Date(dato.fechaHoraFin);
            const durMs = Math.abs(fin - ini);
            const hh = Math.floor(durMs / 3600000);
            const mm = Math.floor((durMs % 3600000) / 60000);
            const ss = Math.floor((durMs % 60000) / 1000);
            const dur = `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
            row.getCell(1).value = linea.nombre;
            row.getCell(2).value = ESTATUS_LABEL[dato.estatus] ?? dato.nombreEstatus ?? '-';
            row.getCell(3).value = dato.folio ?? '';
            row.getCell(4).value = ini.toLocaleString();
            row.getCell(5).value = fin.toLocaleString();
            row.getCell(6).value = dur;
            if (altToggle) {
                row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
            }
            altToggle = !altToggle;
            row.height = 16;
            rowIdx++;
        });
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `linea_tiempo_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

const agregarCeroSiEsNecesario = (valor) => (valor < 10 ? `0${valor}` : `${valor}`);

export default function LineaTiempoV2() {
    const { statusLinea, isLoadingLlamadas, selectedArea, selectedLinea, setExportFn } = useReportes();

    const [chartData, setChartData] = useState([]);
    const [minX, setMinX] = useState(null);
    const [maxX, setMaxX] = useState(null);

    const isUserZoomedRef = useRef(false);
    const zoomRangeRef = useRef({ min: undefined, max: undefined });

    const chartHeight = useMemo(() => {
        const rows = statusLinea.length;
        return rows > 0 ? Math.min(600, Math.max(200, rows * 56)) : 200;
    }, [statusLinea]);

    const parseApiDateAsUtc = useCallback((value) => {
        if (value == null) return NaN;
        const str = typeof value === 'string' ? value.trim() : String(value);
        if (!str) return NaN;
        const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(str);
        const toParse = hasTimezone ? str : `${str}Z`;
        const ts = Date.parse(toParse);
        return Number.isFinite(ts) ? ts : NaN;
    }, []);

    const summary = useMemo(() => {
        const totals = {};
        statusLinea.forEach((linea) => {
            linea.datos.forEach((dato) => {
                const ini = parseApiDateAsUtc(dato.fechaHoraIni);
                const fin = parseApiDateAsUtc(dato.fechaHoraFin);
                if (!Number.isFinite(ini) || !Number.isFinite(fin)) return;
                const dur = Math.abs(fin - ini);
                // Estatus > 6 se trata como Paro no Planeado (estatus 4)
                const s = Number(dato.estatus) > 6 ? 4 : Number(dato.estatus);
                totals[s] = (totals[s] || 0) + dur;
            });
        });
        const total = Object.values(totals).reduce((a, b) => a + b, 0);
        const orderedKeys = STATUS_ORDER.filter((s) => s in totals);
        return { totals, total, orderedKeys };
    }, [statusLinea, parseApiDateAsUtc]);

    const msToHMS = useCallback((ms) => {
        let seconds = ms / 1000;
        const hours = parseInt(seconds / 3600);
        seconds = seconds % 3600;
        const minutes = parseInt(seconds / 60);
        seconds = seconds % 60;
        return `${agregarCeroSiEsNecesario(hours)}:${agregarCeroSiEsNecesario(minutes)}:${agregarCeroSiEsNecesario(Math.floor(seconds))}`;
    }, []);

    useEffect(() => {
        const data1 = [];
        let min = null;
        let max = null;

        statusLinea.forEach((estatusL) => {
            estatusL.datos.forEach((dato) => {
                const rawIni = parseApiDateAsUtc(dato.fechaHoraIni);
                const rawFin = parseApiDateAsUtc(dato.fechaHoraFin);
                if (!Number.isFinite(rawIni) || !Number.isFinite(rawFin)) return;
                const start = Math.min(rawIni, rawFin);
                const end = Math.max(rawIni, rawFin);
                const diff = msToHMS(end - start);

                if (min === null || start < min) min = start;
                if (max === null || end > max) max = end;

                data1.push({
                    name: dato.nombreEstatus,
                    data: [{ x: estatusL.nombre, y: [start, end], z: [dato.estatus, diff, dato.folio] }],
                });
            });
        });

        setChartData(data1);
        setMinX(min);
        setMaxX(max);
    }, [statusLinea, msToHMS, parseApiDateAsUtc]);

    useEffect(() => {
        if (statusLinea.length > 0) {
            setExportFn(() => () => exportarExcel(statusLinea, selectedArea, selectedLinea));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [statusLinea, selectedArea, selectedLinea, setExportFn]);

    const options = useMemo(() => ({
        chart: {
            height: chartHeight,
            type: 'rangeBar',
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
            id: 'rangeBarLT',
            toolbar: { show: true, offsetX: 0, offsetY: chartHeight - 14 },
            parentHeightOffset: 0,
            offsetY: -10,
            events: {
                zoomed: function (_ctx, { xaxis }) {
                    isUserZoomedRef.current = true;
                    zoomRangeRef.current = { min: xaxis.min, max: xaxis.max };
                },
                scrolled: function (_ctx, { xaxis }) {
                    isUserZoomedRef.current = true;
                    zoomRangeRef.current = { min: xaxis.min, max: xaxis.max };
                },
                beforeResetZoom: function () {
                    isUserZoomedRef.current = false;
                    zoomRangeRef.current = { min: undefined, max: undefined };
                    return { xaxis: { min: minX, max: maxX } };
                },
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: { enabled: true, delay: 150 },
                dynamicAnimation: { enabled: true, speed: 500 },
            },
        },
        plotOptions: {
            bar: { horizontal: true, barHeight: '75%', rangeBarGroupRows: true },
        },
        noData: {
            text: isLoadingLlamadas ? 'Cargando información...' : 'No existen datos para esta selección',
            align: 'center',
            verticalAlign: 'middle',
            offsetX: 0,
            offsetY: 0,
            style: { fontSize: '18px', fontFamily: 'WorkSans', fontWeight: 600, color: '#5f606f' },
        },
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            min: isUserZoomedRef.current ? zoomRangeRef.current.min : minX,
            max: isUserZoomedRef.current ? zoomRangeRef.current.max : maxX,
            labels: {
                show: true,
                datetimeUTC: false,
                style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' },
            },
        },
        yaxis: {
            labels: {
                show: true,
                style: { colors: '#5f606f', fontSize: '13px', fontFamily: 'WorkSans', fontWeight: 600 },
            },
        },
        grid: {
            show: true,
            borderColor: '#90A4AE',
            strokeDashArray: 0,
            position: 'back',
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        tooltip: {
            followCursor: true,
            shared: false,
            intersect: false,
            x: { show: true, format: 'dd MMM HH:mm', formatter: undefined },
            fixed: { enabled: false },
            custom: function ({ seriesIndex, dataPointIndex, w }) {
                const point = w.config.series[seriesIndex].data[dataPointIndex];
                if (!point || !Array.isArray(point.y) || point.y.length < 2) return '';
                const fechaIni = new Date(point.y[0]);
                const fechaFin = new Date(point.y[1]);
                const duracion = point.z?.[1] ?? '';
                return (
                    `<div class="Parostooltip"><div><h3>${w.config.series[seriesIndex].name}</h3></div><div><h4>Fecha inicial: ${fechaIni.toLocaleString()}</br>Fecha final: ${fechaFin.toLocaleString()}</br>Duración: ${duracion}</h4></div></div>`
                );
            },
        },
        colors: [
            function ({ seriesIndex, w }) {
                if (w.globals.seriesZ[seriesIndex].length > 0) {
                    const s = w.globals.seriesZ[seriesIndex][0][0];
                    if (s === 0) return '#8A8A8A';
                    if (s === 1) return '#2ccb9e';
                    if (s === 2) return '#ffc800';
                    if (s === 3) return '#FE93C2';
                    if (s === 4) return '#94A3B8';
                    if (s === 5) return '#d3acac';
                    return '#fe424d';
                }
                return '#fe424d';
            },
        ],
        legend: { show: false },
    }), [minX, maxX, chartHeight, isLoadingLlamadas]);

    return (
        <div className="lt-container">
            <div className="lt-header-row">
                <span className="lt-section-title">Línea de Tiempo</span>
                {selectedArea  && <span className="ro-info-chip">{selectedArea.nombre}</span>}
                {selectedLinea && <span className="ro-info-chip">{selectedLinea.nombre}</span>}
            </div>

            {statusLinea.length > 0 && summary.orderedKeys.length > 0 && (
                <div className="lt-summary-row">
                    {summary.orderedKeys.map((s) => {
                        const ms = summary.totals[s];
                        const pct = summary.total > 0 ? (ms / summary.total) * 100 : 0;
                        const label = ESTATUS_LABEL[s] ?? `Estatus ${s}`;
                        return (
                            <div key={s} className="lt-summary-card" data-status={s}>
                                <span className="lt-summary-card__label">{label}</span>
                                <span className="lt-summary-card__value">{msToHM(ms)}</span>
                                <span className="lt-summary-card__pct" data-status={s}>
                                    {pct.toFixed(1)}% del total
                                </span>
                                <div className="lt-summary-card__accent" data-status={s} />
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="lt-card">
            <div className="lt-legend">
                <div className="lt-legend-item">
                    <FiberManualRecordIcon className="lt-legend-dot lt-legend-dot--fuera" />
                    <h4>Fuera</h4>
                </div>
                <div className="lt-legend-item">
                    <FiberManualRecordIcon className="lt-legend-dot lt-legend-dot--operando" />
                    <h4>Operando</h4>
                </div>
                <div className="lt-legend-item">
                    <FiberManualRecordIcon className="lt-legend-dot lt-legend-dot--setup" />
                    <h4>Setup</h4>
                </div>
                <div className="lt-legend-item">
                    <FiberManualRecordIcon className="lt-legend-dot lt-legend-dot--planeado" />
                    <h4>Paro Planeado</h4>
                </div>
                <div className="lt-legend-item">
                    <FiberManualRecordIcon className="lt-legend-dot lt-legend-dot--noplaneado" />
                    <h4>Paro no Planeado</h4>
                </div>
            </div>
            <div className="lt-chart-wrapper">
                <Chart
                    options={options}
                    series={chartData}
                    type="rangeBar"
                    height={chartHeight}
                    width="100%"
                />
            </div>
            </div>
        </div>
    );
}
